import { useContext, useState, ClassAttributes, HTMLAttributes } from 'react';
import { Row, Col } from 'reactstrap';
import { Table, Column, AutoSizer, InfiniteLoader } from 'react-virtualized';
import { Filter } from 'common/Filter';
import { Search } from 'common/Search';
import PageContainer from 'common/PageContainer';
import { FILTERS } from './constant';
import { CustomersContext } from './context';
import PageLoader from 'common/PageLoader';
import { PersonCircle } from 'react-bootstrap-icons';
import { useNavigate } from 'react-router';
import Header from 'components/Header';
import NoData from 'common/NoData';
import moment from 'moment';
import { DatePicker } from 'common/DatePicker';
import { DATES } from 'common/DatePicker/dateConstant';
import ExportExcel from 'components/ExportExcel/ExportExcel';
import { customerExcelConfig } from 'utils/excel/customerExcelConfig';
import { formatCustomerData } from 'utils/excelFormat/formatCustomerData';

const GridFilterBar = ({
  searchKeyword,
  filterCriteria,
  handleSearch,
  allData,
}: {
  searchKeyword: string;
  filterCriteria: any;
  handleSearch: (searchTerm: string) => void;
  allData: any[];
}) => {

  const formattedData = formatCustomerData(allData);

  return (
    <Row className="mb-2 mt-2">
      <Col sm={12} md={3}>
        <Search searchKeyword={searchKeyword} onSearch={handleSearch} />
      </Col>
      <Col sm={12} md={6} className="d-flex align-items-center">
        <Filter filterCriteria={filterCriteria} />
      </Col>
      <Col className="d-flex justify-content-around">
        <DatePicker filterCriteria={DATES} />
        {allData && allData.length > 0 && (
          <ExportExcel allData={formattedData} config={customerExcelConfig} />
        )}
      </Col>
    </Row>
  );
};

// Row Renderer
const rowRenderer = (props: {
  className: any;
  columns: any;
  index?: number;
  isScrolling?: boolean;
  key?: string;
  onRowClick?: (params: any) => void;
  rowData: any;
  style: any;
}) => {
  const { columns, className, rowData, style, onRowClick } = props;
  const { id } = rowData;

  if (!rowData) {
    console.warn('Row data is missing:', props);
    return null;
  }

  console.log('row Data....', rowData);

  const isClickable = rowData.status !== null && rowData.status !== undefined;

  const address = rowData?.address
    ? `${rowData.address.address || ''}, ${rowData.address.city || ''}, ${
        rowData.address.zip || ''
      },[${rowData.address.region}]`
    : '-';

  return (
    // <div className={className} style={style} onClick={() => onRowClick?. (id)} key={customer-row-${id}}>
    <div
      className={`${className} ${isClickable ? '' : 'disabled-row'}`}
      style={style}
      onClick={isClickable ? () => onRowClick?.(id) : undefined}
      key={`customer-row-${id}`}
    >
      {columns.map(
        (col: {
          props: JSX.IntrinsicAttributes &
            ClassAttributes<HTMLDivElement> &
            HTMLAttributes<HTMLDivElement>;
        }) => {
          const keyName: any = col.props['aria-describedby'];
          // const keyName: any = col.props["dataKey"];
          let value = rowData[keyName];
          // let value = rowData[dataKey];

          if (typeof value === 'object' && value !== null) {
            value = JSON.stringify(value);
          }

          if (keyName === 'Name' || keyName === 'name') {
            const fullName = rowData.name?.trim() || '-';
            return (
              <div
                {...col.props}
                title={fullName}
                key={`${keyName}-cell-${id}`}
              >
                <PersonCircle className="me-2" size={20} />
                <span>{fullName}</span>
              </div>
            );
          }

          if (keyName === 'createdAt') {
            value = rowData?.createdAt
              ? moment(rowData.createdAt, 'YYYY-MM-DD HH:mm:ss').format(
                  'DD/MM/YYYY, hh:mm A',
                )
              : 'N/A';
          }

          if (keyName === 'address') {
            return (
              <div {...col.props} title={address} key={`${keyName}-cell-${id}`}>
                {address}
              </div>
            );
          }

          if (!keyName || !rowData[keyName] === null) {
            return (
              <div
                {...col.props}
                key={`${col.props['aria-describedby']}-cell-${id}`}
              >
                {' '}
                -{' '}
              </div>
            );
          }
          return (
            <div
              {...col.props}
              title={value || '-'}
              key={`${col.props['aria-describedby']}-cell-${id}`}
            >
              {value}
            </div>
          );
        },
      )}
    </div>
  );
};

const Customers = () => {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState('');

  const context = useContext(CustomersContext);

  if (!context) return null;

  const {
    userData: { isLoading, status, error, data: users },
  } = context;

  // const {
  //   userData: { isLoading, status, error, data: users },
  // } = useContext(CustomersContext);

  const filteredUsers = users
    ?.map((user: any) => {
      const name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
      return {
        ...user,
        name, // Injecting full name as `name` key
      };
    })
    .filter((user: any) => {
      const trimmedSearchText = searchText.trim().toLowerCase();
      if (!trimmedSearchText) return true;

      const name = user.name.toLowerCase();
      const email = user.email?.toLowerCase() || '';
      const phone = user.phone?.toString() || '';

      return (
        name.includes(trimmedSearchText) ||
        email.includes(trimmedSearchText) ||
        phone.includes(trimmedSearchText)
      );
    });

  const loadMore = ({
    startIndex,
    stopIndex,
  }: {
    startIndex: number;
    stopIndex: number;
  }) => {
    console.log('loadMore called', startIndex, stopIndex);

    return new Promise<void>((resolve) => {
      setTimeout(() => {
        actuallyLoadMore();
        resolve();
      }, 500);
    });
  };

  const actuallyLoadMore = () => {
    console.log('actuallyLoadMore Called!!');
  };
  const handleRowClick = (customerId: any) =>
    navigate(`${customerId}/details`, {
      state: { heading: 'Customer Details', iconClass: 'bi bi-chevron-left' },
    });

  const handleSearchChange = (searchTerm: string) => {
    setSearchText(searchTerm);
  };

  if (isLoading) return <PageLoader />;
  if (error) {
    return <span>Something went wrong!</span>;
  }
  if (status === 200 && !users?.length) return <span>No data found!</span>;
  if (status === 200) {
    const totalCount = filteredUsers?.length;
    return (
      <PageContainer>
        <Header totalCount={totalCount} heading={'Customers'} />
        <GridFilterBar
          searchKeyword={searchText}
          filterCriteria={FILTERS}
          handleSearch={handleSearchChange}
          allData={filteredUsers}
        />
        <Row className="mt-4">
          <Col>
            {filteredUsers && filteredUsers.length > 0 ? (
              <InfiniteLoader
                isRowLoaded={({ index }) => !!filteredUsers?.[index]}
                loadMoreRows={loadMore}
                rowCount={filteredUsers?.length}
              >
                {({ onRowsRendered, registerChild }) => (
                  <AutoSizer>
                    {({ width }) => (
                      <Table
                        ref={registerChild}
                        onRowsRendered={onRowsRendered}
                        rowClassName="table-row"
                        headerHeight={32}
                        width={width}
                        height={650}
                        rowHeight={45}
                        rowCount={filteredUsers.length}
                        rowGetter={({ index }) => filteredUsers?.[index]}
                        onRowClick={handleRowClick}
                        rowRenderer={rowRenderer}
                      >
                        <Column
                          label="Name"
                          dataKey="name"
                          id="name"
                          width={width * 0.3}
                        />
                        <Column
                          id="email"
                          label="Email"
                          dataKey="email"
                          width={width * 0.3}
                        />
                        <Column
                          id="phone"
                          label="Phone"
                          dataKey="phone"
                          width={width * 0.3}
                        />
                        <Column
                          id="createdAt"
                          label="Create Date"
                          dataKey="createdAt"
                          width={width * 0.3}
                        />
                        <Column
                          id="address"
                          label="Address"
                          dataKey="address"
                          width={width * 0.3}
                        />
                        <Column
                          id="status"
                          label="Status"
                          dataKey="status"
                          width={width * 0.1}
                        />
                      </Table>
                    )}
                  </AutoSizer>
                )}
              </InfiniteLoader>
            ) : (
              <NoData customMessage={'No result found. '} />
            )}
          </Col>
        </Row>
      </PageContainer>
    );
  }

  return null;
};

export default Customers;
